import { CategoryButton } from '@/components/feature/floor-guide';
import type { KioskSelectedCategory } from '@/services/dto';
import { filterCategoriesWithMapPoi, mergeSelectedCategoriesByClientCode } from '@/utils/floor-guide';
import type { GroupedPoiByBuildingId } from '@/types/map';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { useAccessibilityTTS } from '@/hooks';
import { NAV_KEYS } from '@/constants/keyboard';
import { useConfigStore } from '@/stores';

type Props = {
    onSelectCategory?: (category: KioskSelectedCategory) => void;
    groupedPoiByBuildingId: GroupedPoiByBuildingId;
    disabled?: boolean;
    selectedCategoryCode?: string;
};

export const HorizontalCategoryList = ({
    onSelectCategory,
    groupedPoiByBuildingId,
    disabled = false,
    selectedCategoryCode,
}: Props) => {
    const { t, i18n } = useTranslation();
    const language = useMemo(() => i18n.language, [i18n]);
    const { speak } = useAccessibilityTTS();
    const listRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const selectedCategories = useConfigStore(
        (state) => state.config.server?.buildingMenus?.[language]?.find((menu) => menu.menuType === 'FLOOR')?.selectedCategories
    );
    const buildingCategories = useMemo(() => {
        const filtered = filterCategoriesWithMapPoi(selectedCategories, groupedPoiByBuildingId);
        return mergeSelectedCategoriesByClientCode(filtered);
    }, [selectedCategories, groupedPoiByBuildingId]);

    const checkOverflow = useCallback(() => {
        if (listRef.current) {
            const { scrollWidth, clientWidth } = listRef.current;
            setIsOverflowing(scrollWidth > clientWidth);
        }
    }, []);

    useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [checkOverflow, buildingCategories]);

    return (
        <section className={styles['horizontal-category__wrapper']}>
            <div
                ref={listRef}
                className={clsx(styles['horizontal-category__list'], 'scrollbar-hide', {
                    'justify-center': !isOverflowing,
                    'justify-start px-[calc(30*var(--vw-unit))]': isOverflowing,
                })}
                data-region-nav
                data-region-order={6}
                data-tts-scope="floor-button"
            >
                {Array.isArray(buildingCategories) &&
                    buildingCategories.length > 0 &&
                    buildingCategories.map((item, index) => (
                        <div
                            key={`${item.clientCode}-${index}`}
                            className="shrink-0"
                        >
                            <CategoryButton
                                isSelected={selectedCategoryCode === item.categoryCode}
                                tabIndex={disabled ? -1 : 6}
                                onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                    if (disabled || !NAV_KEYS.includes(e.key)) return;

                                    speak(
                                        t('floor_guide.text_to_speech.category_btn', {
                                            categoryName: item?.name || '',
                                        })
                                    );
                                }}
                                data-playsound="ting"
                                data-interactive
                                onClick={() => {
                                    if (disabled) return;
                                    onSelectCategory?.(item);
                                }}
                            >
                                {item?.name || item?.name || ''}
                            </CategoryButton>
                        </div>
                    ))}
            </div>
        </section>
    );
};
